import React, { useContext, useState, useEffect, useMemo } from 'react'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import FormField from 'components/FormField'
import ValuePoint from 'admin/components/ValuePoint'

import {
  Card,
  Grid,
  Typography,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Divider,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'

import { differenceInYears, differenceInMonths, addMonths } from 'date-fns'
import { useFegliCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useFegliCalculations'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'
import PrivateLifeInsurance from 'admin/components/PrivateLifeInsurance'
import { DataGrid } from '@mui/x-data-grid'
import LifeInsuranceSummary from 'layouts/authenticated/pages/fegli/components/LifeInsuranceSummary'
import { useModal } from 'components/modals'

const dateToYears = (startDate, endDate) => {
  const years =
    startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  const diff =
    startDate && endDate
      ? differenceInMonths(new Date(endDate), new Date(startDate))
      : ''
  const months =
    startDate && endDate
      ? differenceInMonths(
          new Date(endDate),
          addMonths(new Date(startDate), years * 12)
        )
      : ''
  return { years, months }
}

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function OptionCReimbursement(status, dependents) {
  const isNotMarried = status !== 'married'
  const noQualifyingKids =
    dependents.length === 0 || dependents.every((dep) => dep.age >= 26)
  return isNotMarried && noQualifyingKids
}

function Fegli({
  collapseSections,
  toggleCollapse,
  legacyScore,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
}) {
  const { openModal } = useModal()
  const isGovernmentEmployee = intakeState?.governmentEmployee
  const plannedRetirementAge = Math.floor(
    differenceInMonths(
      new Date(intakeState.dateOfPlannedRetirement),
      new Date(intakeState.dateOfBirth)
    ) / 12
  )
  const [fegliOptionAgeRange, setFegliOptionAgeRange] =
    useState(plannedRetirementAge)
  const switchFegliOptionAgeRange = (event, newAgeRange) => {
    newAgeRange === null
      ? setFegliOptionAgeRange(plannedRetirementAge)
      : setFegliOptionAgeRange(newAgeRange)
  }
  const [localForm, setLocalForm] = useState({
    useFegliFinder: false,
    fegliFinderAmount: intakeState.fegliFinderAmount || '',
  })
  const handleChange = (field) => (value) => {
    console.log('inHandleChange')
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const handleSwitchChange = (key) => (e) => {
    const updates = { [key]: e.target.checked }

    if (key === 'fegliOptionB') {
      updates.fegliOptionBMultiples = 0
    } else if (key === 'fegliOptionC') {
      updates.fegliOptionCMultiples = 0
    }

    updateIntakeStateAndSubmit(() => updateIntakeStateAtKey(updates))()
  }

  const {
    bedb,
    basicLife,
    optionA,
    optionB,
    optionC,
    fegliTotal,
    privateInsurance,
    allLifeInsuranceTotal,
    totalEstate,
    grossCoverageNeeded,
    getRankedFEGLICombos,
  } = useFegliCalculations(fegliOptionAgeRange)

  const showFEGLI = intakeState?.fegli && isGovernmentEmployee
  const lifeInsuranceOther = intakeState?.lifeInsuranceOther

  const rankedCombos = useMemo(() => {
    if (!intakeState?.fegliFinderAmount) return []
    return getRankedFEGLICombos({
      targetPremium: ((intakeState.fegliFinderAmount / 80) * 2087) / 12 || 0,
      maxResults: 2,
    })
  }, [intakeState.fegliFinderAmount, getRankedFEGLICombos])

  const columns = [
    { field: 'optionA', headerName: 'Option A', type: 'boolean', flex: 1 },
    { field: 'optionBMultiples', headerName: 'Option B', flex: 1 },
    { field: 'optionCMultiples', headerName: 'Option C', flex: 1 },
    {
      field: 'total',
      headerName: 'Total Premium',
      flex: 1,
      renderCell: (params) => {
        const value = params.value
        return typeof value === 'number' ? `$${value.toFixed(2)}` : ''
      },
    },
    {
      field: 'difference',
      headerName: 'Diff from Target',
      flex: 1,
      renderCell: (params) => {
        const value = params.value
        return typeof value === 'number' ? `$${value.toFixed(2)}` : ''
      },
    },
  ]

  const buttonSx = {
    borderRadius: '36px !important',
    border: 'none',
    color: '#fff',
    textTransform: 'capitalize',
    fontWeight: '500',
    letterSpacing: '0.5px',
    '&.Mui-selected': {
      backgroundColor: '#fff !important',
      color: '#75787B',
      fontWeight: '600',
    },
  }
  const ageOptions = [
    { value: 60, label: 'Before Age 65' },
    { value: 65, label: '65-70' },
    { value: 70, label: '70-75' },
    { value: 75, label: '75-80' },
    { value: 80, label: '80+' },
  ]
  const currentAge = dateToYears(intakeState.dateOfBirth, new Date())?.years

  const [tableTimeHorizon, setTableTimeHorizon] = useState('monthly')
  const switchTimeHorizon = (event, newTimeHorizon) => {
    newTimeHorizon === null
      ? setTableTimeHorizon('monthly')
      : setTableTimeHorizon(newTimeHorizon)
  }

  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse in={collapseSections.fegli} style={{ width: '100%' }}>
                {dateToYears(intakeState.dateOfBirth, new Date()).years <
                  55 && (
                  <>
                    {legacyScore >= 100 ? (
                      <ValuePoint text="Fully Covered" type="success" />
                    ) : (
                      <ValuePoint
                        text={`Legacy Coverage ${legacyScore}%`}
                        type="donut"
                      />
                    )}
                  </>
                )}

                {intakeState?.fegliOptionC &&
                  OptionCReimbursement(
                    intakeState?.familyMaritalStatus,
                    intakeState?.familyDependents
                      ? intakeState?.familyDependents
                      : []
                  ) && (
                    <ValuePoint
                      text="Pursue Option C Reimbursement"
                      type="warning"
                    />
                  )}
                {intakeState?.familyMaritalStatus === 'widow' &&
                  intakeState?.fegliOptionC && (
                    <ValuePoint
                      text="Did they collect death benefit?"
                      type="warning"
                    />
                  )}
                {intakeState?.fegliOptionB && (
                  <ValuePoint text="Consider Private L.I. Alternatives to Option B" />
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="fegli"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse('fegli')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                {isGovernmentEmployee
                  ? 'FEGLI & Life Insurance'
                  : 'Life Insurance'}
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.fegli
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse in={collapseSections.fegli} style={{ width: '100%' }}>
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              {isGovernmentEmployee && (
                <>
                  <Grid item xs={12} sm={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'fegli',
                      label: 'Are you currently enrolled in FEGLI coverage?',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.fegli,
                      onChange: updateIntakeStateAndSubmit((fegli) =>
                        updateIntakeStateAtKey({ fegli })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'useFegliFinder',
                      label: 'Use FEGLI Solver?',
                      placeholder: '',
                      inputType: 'select',
                      value: localForm.useFegliFinder,
                      onChange: (e) => handleChange('useFegliFinder')(e),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      shrink: true,
                      id: 'fegliFinderAmount',
                      label: 'FEGLI Amount Per Pay Period',
                      placeholder: '$100.00',
                      inputType: 'currencyFloat',
                      value: localForm.fegliFinderAmount,
                      onChange: (e) => handleChange('fegliFinderAmount')(e),
                      onBlur: updateIntakeStateAndSubmit((fegliFinderAmount) =>
                        updateIntakeStateAtKey({ fegliFinderAmount })
                      ),
                    })}
                  </Grid>
                  {localForm.useFegliFinder && (
                    <>
                      <Grid item xs={12} mt={2}>
                        <Typography variant="h5">FEGLI Solver</Typography>
                      </Grid>
                      {intakeState?.fegliFinderAmount ? (
                        <>
                          <Grid
                            item
                            xs={4}
                            sm={2}
                            display={'flex'}
                            alignItems={'center'}
                          >
                            <Typography variant="body2Medium">
                              Option A
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={5}>
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'optionAInfo',
                              label: 'Option A Coverage',
                              inputType: 'text',
                              value: rankedCombos[0]?.optionA ? 'Yes' : 'No',
                            })}
                          </Grid>
                          <Grid item xs={4} sm={5}>
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'optionAInfo',
                              label: 'Option A Coverage',
                              inputType: 'text',
                              value: rankedCombos[1]?.optionA ? 'Yes' : 'No',
                            })}
                          </Grid>

                          <Grid
                            item
                            xs={4}
                            sm={2}
                            display={'flex'}
                            alignItems={'center'}
                          >
                            <Typography variant="body2Medium">
                              Option B
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={5}>
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'optionAInfo',
                              label: 'Multiples of Option B',
                              inputType: 'text',
                              value: rankedCombos[0]?.optionBMultiples
                                ? rankedCombos[0].optionBMultiples
                                : '',
                            })}
                          </Grid>
                          <Grid item xs={4} sm={5}>
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'optionAInfo',
                              label: 'Multiples of Option B',
                              inputType: 'text',
                              value: rankedCombos[1]?.optionBMultiples
                                ? rankedCombos[1].optionBMultiples
                                : '',
                            })}
                          </Grid>

                          <Grid
                            item
                            xs={4}
                            sm={2}
                            display={'flex'}
                            alignItems={'center'}
                          >
                            <Typography variant="body2Medium">
                              Option C
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={5}>
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'optionAInfo',
                              label: 'Units of Option C',
                              inputType: 'text',
                              value: rankedCombos[0]?.optionCMultiples
                                ? rankedCombos[0].optionCMultiples
                                : '',
                            })}
                          </Grid>
                          <Grid item xs={4} sm={5}>
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'optionAInfo',
                              label: 'Units of Option C',
                              inputType: 'text',
                              value: rankedCombos[1]?.optionCMultiples
                                ? rankedCombos[1].optionCMultiples
                                : '',
                            })}
                          </Grid>
                          <Grid
                            item
                            xs={4}
                            sm={2}
                            display={'flex'}
                            alignItems={'center'}
                          >
                            <Typography
                              variant="body2Medium"
                              sx={{
                                width: '100%',
                                borderTop: '2px solid transparent',
                                paddingTop: 1,
                              }}
                            >
                              Total Cost
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={4}
                            sm={5}
                            display={'flex'}
                            alignItems={'center'}
                          >
                            <Typography
                              variant="body2Medium"
                              sx={{
                                width: '100%',
                                borderTop: '2px solid #75787B',
                                paddingTop: 1,
                              }}
                            >
                              {rankedCombos[0]?.total
                                ? dollarCentsFormat.format(
                                    ((rankedCombos[0].total * 12) / 2087) * 80
                                  )
                                : '$0.00'}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={4}
                            sm={5}
                            display={'flex'}
                            alignItems={'center'}
                          >
                            <Typography
                              variant="body2Medium"
                              sx={{
                                width: '100%',
                                borderTop: '2px solid #75787B',
                                paddingTop: 1,
                              }}
                            >
                              {rankedCombos[1]?.total
                                ? dollarCentsFormat.format(
                                    ((rankedCombos[1].total * 12) / 2087) * 80
                                  )
                                : '$0.00'}
                            </Typography>
                          </Grid>
                        </>
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body2Medium">
                            Add a FEGLI Amount Per Pay Period above to view the
                            solver.
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ background: '#ddd' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      mb={2}
                    >
                      <Typography variant="h5">
                        Current FEGLI Coverage
                      </Typography>
                      <ToggleButtonGroup
                        value={tableTimeHorizon}
                        exclusive
                        onChange={switchTimeHorizon}
                        aria-label="TimeHorizon"
                        sx={{
                          height: '36px',
                          alignSelf: 'center',
                          borderRadius: '36px',
                          background: '#A3A7AA',
                          border: '3px solid #A3A7AA',
                        }}
                      >
                        <ToggleButton
                          sx={{
                            borderRadius: '36px !important',
                            border: 'none',
                            color: '#fff',
                            textTransform: 'capitalize',
                            fontWeight: '500',
                            letterSpacing: '0.5px',
                            '&.Mui-selected': {
                              backgroundColor: '#fff !important',
                              color: '#75787B',
                              fontWeight: '600',
                            },
                          }}
                          value="monthly"
                        >
                          Monthly
                        </ToggleButton>
                        <ToggleButton
                          sx={{
                            borderRadius: '36px !important',
                            border: 'none',
                            color: '#fff',
                            textTransform: 'capitalize',
                            fontWeight: '500',
                            letterSpacing: '0.5px',
                            '&.Mui-selected': {
                              backgroundColor: '#fff !important',
                              color: '#75787B',
                              fontWeight: '600',
                            },
                          }}
                          value="payPeriod"
                        >
                          Pay Period
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!intakeState.fegli}
                          onChange={handleSwitchChange('fegli')}
                          name="fegli"
                          disabled
                          sx={{
                            transform: 'scale(1.3)', // makes switch bigger
                            marginLeft: '16px',
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#2370ED !important', // track color when checked
                              },
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2Medium"
                          sx={{ marginLeft: '16px' }}
                        >
                          Basic Life
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'basicLifeInfo',
                          label: 'Basic Life Option',
                          inputType: 'text',
                          value: 'Only One Option',
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionAInfo',
                          label: 'Basic Life Coverage',
                          inputType: 'text',
                          value: dollarFormat.format(basicLife?.coverage || 0),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionAInfo',
                          label:
                            tableTimeHorizon === 'monthly'
                              ? 'Basic Life Monthly Cost'
                              : 'Basic Life Pay Period Cost',
                          inputType: 'text',
                          value: dollarCentsFormat.format(
                            (tableTimeHorizon === 'monthly'
                              ? basicLife?.premium
                              : ((basicLife?.premium * 12) / 2087) * 80) || 0
                          ),
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Option A */}
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!intakeState.fegliOptionA}
                          onChange={handleSwitchChange('fegliOptionA')}
                          name="optionA"
                          sx={{
                            transform: 'scale(1.3)', // makes switch bigger
                            marginLeft: '16px',
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#2370ED !important', // track color when checked
                              },
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2Medium"
                          sx={{ marginLeft: '16px' }}
                        >
                          Option A
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'basicLifeInfo',
                          label: 'Option A Option',
                          inputType: 'text',
                          value: 'Only One Option',
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionAInfo',
                          label: 'Option A Coverage',
                          inputType: 'text',
                          value: dollarFormat.format(
                            intakeState?.fegliOptionA ? 10000 : 0
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionAInfo',
                          label:
                            tableTimeHorizon === 'monthly'
                              ? 'Option A Monthly Cost'
                              : 'Option A Pay Period Cost',
                          inputType: 'text',
                          value: dollarCentsFormat.format(
                            (tableTimeHorizon === 'monthly'
                              ? optionA?.premium
                              : ((optionA?.premium * 12) / 2087) * 80) || 0
                          ),
                        })}
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Option B */}
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!intakeState.fegliOptionB}
                          onChange={handleSwitchChange('fegliOptionB')}
                          name="optionB"
                          sx={{
                            transform: 'scale(1.3)', // makes switch bigger
                            marginLeft: '16px',
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#2370ED !important', // track color when checked
                              },
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2Medium"
                          sx={{ marginLeft: '16px' }}
                        >
                          Option B
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'fegliOptionBMultiples',
                          label: 'Multiples of Option B',
                          placeholder: '(ranges from 0-5)',
                          inputType: 'select',
                          value: intakeState.fegliOptionBMultiples,
                          onChange: updateIntakeStateAndSubmit(
                            (fegliOptionBMultiples) =>
                              updateIntakeStateAtKey({ fegliOptionBMultiples })
                          ),
                          options: [
                            { value: 0, label: '0' },
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5' },
                          ],
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionBInfo',
                          label: 'Option B Coverage',
                          inputType: 'text',
                          value: dollarCentsFormat.format(
                            optionB?.coverage || 0
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionBInfo',
                          label:
                            tableTimeHorizon === 'monthly'
                              ? 'Option B Monthly Cost'
                              : 'Option B Pay Period Cost',
                          inputType: 'text',
                          value: dollarCentsFormat.format(
                            (tableTimeHorizon === 'monthly'
                              ? optionB?.premium
                              : ((optionB?.premium * 12) / 2087) * 80) || 0
                          ),
                        })}
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Option C */}
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!intakeState.fegliOptionC}
                          onChange={handleSwitchChange('fegliOptionC')}
                          name="optionC"
                          sx={{
                            transform: 'scale(1.3)', // makes switch bigger
                            marginLeft: '16px',
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#2370ED !important', // track color when checked
                              },
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2Medium"
                          sx={{ marginLeft: '16px' }}
                        >
                          Option C
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'fegliOptionCMultiples',
                          label: 'Units of Option C',
                          placeholder: '(ranges from 0-5)',
                          inputType: 'select',
                          value: intakeState.fegliOptionCMultiples,
                          onChange: updateIntakeStateAndSubmit(
                            (fegliOptionCMultiples) =>
                              updateIntakeStateAtKey({ fegliOptionCMultiples })
                          ),
                          options: [
                            { value: 0, label: '0' },
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5' },
                          ],
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionCInfo',
                          label: 'Option C Coverage',
                          inputType: 'text',
                          value: dollarCentsFormat.format(
                            optionC?.coverage || 0
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'optionCInfo',
                          label:
                            tableTimeHorizon === 'monthly'
                              ? 'Option C Monthly Cost'
                              : 'Option C Pay Period Cost',
                          inputType: 'text',
                          value: dollarCentsFormat.format(
                            (tableTimeHorizon === 'monthly'
                              ? optionC?.premium
                              : ((optionC?.premium * 12) / 2087) * 80) || 0
                          ),
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Totals */}
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <Typography variant="body2Medium">
                      {tableTimeHorizon === 'monthly'
                        ? 'Monthly Total'
                        : 'Pay Period Total'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} sm={4}></Grid>
                      <Grid
                        item
                        xs={6}
                        sm={4}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography
                          variant="body2Medium"
                          sx={{
                            width: '100%',
                            borderTop: '2px solid #75787B',
                            paddingTop: 1,
                          }}
                        >
                          Self:{' '}
                          {dollarFormat.format(
                            fegliTotal?.coverage - (bedb ? bedb : 0) || 0
                          )}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sm={4}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography
                          variant="body2Medium"
                          sx={{
                            width: '100%',
                            borderTop: '2px solid #75787B',
                            paddingTop: 1,
                          }}
                        >
                          {dollarCentsFormat.format(
                            (tableTimeHorizon === 'monthly'
                              ? fegliTotal?.premium
                              : ((fegliTotal?.premium * 12) / 2087) * 80) || 0
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* OLD SETUP */}
                  {/* <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'fegli',
                  label: 'Are you currently enrolled in FEGLI coverage?',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.fegli,
                  onChange: updateIntakeStateAndSubmit((fegli) =>
                    updateIntakeStateAtKey({ fegli })
                  ),
                  options: selectData.yesNo,
                })}
                <Typography variant="inputNote" component="p">
                  Basic Life Monthly Cost:{' '}
                  {dollarCentsFormat.format(basicLife?.premium || 0)}
                </Typography>
                <Typography variant="inputNote" component="p">
                  Basic Life Pay Period Cost:{' '}
                  {dollarCentsFormat.format(
                    ((basicLife?.premium * 12) / 2087) * 80 || 0
                  )}
                </Typography>
                <Typography variant="inputNote" component="p">
                  Basic Life Coverage:{' '}
                  {dollarFormat.format(basicLife?.coverage || 0)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6">FEGLI Coverage Totals</Typography>
                <Typography variant="body2" component="p">
                  Total Cost:{' '}
                  {dollarCentsFormat.format(fegliTotal?.premium || 0)}
                </Typography>
                <Typography variant="body2" component="p">
                  Total FEGLI Self Coverage:{' '}
                  {dollarFormat.format(fegliTotal?.coverage || 0)}
                </Typography>
                <Typography variant="body2" component="p">
                  Total FEGLI Coverage (Others):{' '}
                  {dollarFormat.format(optionC?.coverage || 0)}
                </Typography>
              </Grid>

              {showFEGLI && (
                <>
                  <Grid item xs={12} sm={12}>
                    <Typography
                      component="label"
                      variant="body2"
                      fontWeight="regular"
                      textTransform="capitalize"
                    >
                      Do you have any optional FEGLI coverage?
                    </Typography>

                    <OnboardingMultiSelectTile
                      header={false}
                      helpText={'test'}
                      subHeader={false}
                      tileSize={{ xs: '4', sm: '4', md: '4', lg: '4' }}
                      questionArray={[
                        {
                          id: 'fegliOptionA',
                          optionLabel: 'Option A',
                        },
                        {
                          id: 'fegliOptionB',
                          optionLabel: 'Option B',
                        },
                        {
                          id: 'fegliOptionC',
                          optionLabel: 'Option C',
                        },
                      ]}
                    />
                  </Grid>
                  {intakeState?.fegliOptionA ? (
                    <Grid item xs={6} sm={4} alignContent="end">
                      {renderMuiFormField({
                        autoFocus: false,
                        disabled: true,
                        id: 'optionAInfo',
                        label: 'Option A Coverage',
                        inputType: 'text',
                        value: 'Only One Option',
                      })}
                      <Typography variant="inputNote" component="p">
                        Estimated Option A Montly Cost:{' '}
                        {dollarCentsFormat.format(optionA?.premium || 0)}
                      </Typography>
                      <Typography variant="inputNote" component="p">
                        Option A Pay Period Cost:{' '}
                        {dollarCentsFormat.format(
                          ((optionA?.premium * 12) / 2087) * 80 || 0
                        )}
                      </Typography>
                      <Typography variant="inputNote" component="p">
                        Option A Coverage: $10,000
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid item xs={6} sm={4}></Grid>
                  )}

                  {intakeState?.fegliOptionB ? (
                    <Grid item xs={12} sm={4}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'fegliOptionBMultiples',
                        label: 'How many multiples of option B do you have?',
                        placeholder: '(ranges from 0-5)',
                        inputType: 'select',
                        value: intakeState.fegliOptionBMultiples,
                        onChange: updateIntakeStateAndSubmit(
                          (fegliOptionBMultiples) =>
                            updateIntakeStateAtKey({ fegliOptionBMultiples })
                        ),
                        options: [
                          { value: 0, label: '0' },
                          { value: 1, label: '1' },
                          { value: 2, label: '2' },
                          { value: 3, label: '3' },
                          { value: 4, label: '4' },
                          { value: 5, label: '5' },
                        ],
                      })}
                      <Typography variant="inputNote" component="p">
                        Estimated Option B Montly Cost:{' '}
                        {dollarCentsFormat.format(optionB?.premium || 0)}
                      </Typography>
                      <Typography variant="inputNote" component="p">
                        Option B Pay Period Cost:{' '}
                        {dollarCentsFormat.format(
                          ((optionB?.premium * 12) / 2087) * 80 || 0
                        )}
                      </Typography>
                      <Typography variant="inputNote" component="p">
                        Estimated Option B Coverage:{' '}
                        {dollarFormat.format(optionB?.coverage || 0)}
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={4}></Grid>
                  )}
                  {intakeState?.fegliOptionC ? (
                    <Grid item xs={12} sm={4}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'fegliOptionCMultiples',
                        label: 'How many multiples of option C do you have?',
                        placeholder: '(ranges from 0-5)',
                        inputType: 'select',
                        value: intakeState.fegliOptionCMultiples,
                        onChange: updateIntakeStateAndSubmit(
                          (fegliOptionCMultiples) =>
                            updateIntakeStateAtKey({ fegliOptionCMultiples })
                        ),
                        options: [
                          { value: 0, label: '0' },
                          { value: 1, label: '1' },
                          { value: 2, label: '2' },
                          { value: 3, label: '3' },
                          { value: 4, label: '4' },
                          { value: 5, label: '5' },
                        ],
                      })}
                      <Typography variant="inputNote" component="p">
                        Estimated Option C Montly Cost:{' '}
                        {dollarCentsFormat.format(optionC?.premium || 0)}
                      </Typography>
                      <Typography variant="inputNote" component="p">
                        Option C Pay Period Cost:{' '}
                        {dollarCentsFormat.format(
                          ((optionC?.premium * 12) / 2087) * 80 || 0
                        )}
                      </Typography>
                      <Typography variant="inputNote" component="p">
                        Estimated Option C Coverage:{' '}
                        {dollarFormat.format(optionC?.coverage || 0)}
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={4}></Grid>
                  )}
                </>
              )} */}

                  <Grid item xs={12}>
                    <Divider sx={{ background: '#ddd' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      mb={2}
                    >
                      <Typography variant="h5">
                        FEGLI Coverage In Retirement
                      </Typography>
                      <ToggleButtonGroup
                        value={fegliOptionAgeRange}
                        exclusive
                        onChange={switchFegliOptionAgeRange}
                        aria-label="FegliAgeRange"
                        sx={{
                          height: '36px',
                          alignSelf: 'center',
                          borderRadius: '36px',
                          background: '#A3A7AA',
                          border: '3px solid #A3A7AA',
                        }}
                      >
                        <ToggleButton
                          sx={{
                            borderRadius: '36px !important',
                            border: 'none',
                            color: '#fff',
                            textTransform: 'capitalize',
                            fontWeight: '500',
                            letterSpacing: '0.5px',
                            '&.Mui-selected': {
                              backgroundColor: '#fff !important',
                              color: '#75787B',
                              fontWeight: '600',
                            },
                          }}
                          value={plannedRetirementAge}
                        >
                          Planned Retirement Age: {plannedRetirementAge}
                        </ToggleButton>

                        {ageOptions
                          .filter((option) => currentAge < option.value + 5)
                          .map((option) => (
                            <ToggleButton
                              key={option.value}
                              sx={buttonSx}
                              value={option.value}
                            >
                              {option.label}
                            </ToggleButton>
                          ))}
                      </ToggleButtonGroup>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <Typography variant="body2Medium">Basic Life</Typography>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} sm={3}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'fegliExtraBLReduction',
                          label: 'BASIC LIFE Reduction Option',
                          placeholder: 'Select',
                          inputType: 'select',
                          value: intakeState.fegliExtraBLReduction,
                          onChange: updateIntakeStateAndSubmit(
                            (fegliExtraBLReduction) =>
                              updateIntakeStateAtKey({ fegliExtraBLReduction })
                          ),
                          options: [
                            { value: '75%', label: '75%' },
                            { value: '50%', label: '50%' },
                            { value: 'noreduction', label: 'No Reduction' },
                          ],
                        })}
                      </Grid>
                      <Grid item xs={6} sm={3}></Grid>
                      <Grid item xs={6} sm={3} alignContent="end">
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'blc',
                          label: 'BASIC Life Coverage',
                          inputType: 'text',
                          value:
                            basicLife &&
                            dollarFormat.format(
                              basicLife?.retirementCoverage || 0
                            ),
                        })}
                      </Grid>
                      <Grid item xs={6} sm={3} alignContent="end">
                        {renderMuiFormField({
                          autoFocus: false,
                          disabled: true,
                          id: 'blmc',
                          label: 'BASIC Life Monthly Cost',
                          inputType: 'text',
                          value:
                            basicLife &&
                            dollarCentsFormat.format(
                              basicLife?.retirementPremium || 0
                            ),
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                  {intakeState?.fegliOptionA && (
                    <>
                      <Grid
                        item
                        xs={12}
                        sm={2}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography variant="body2Medium">Option A</Typography>
                      </Grid>
                      <Grid item xs={12} sm={10}>
                        <Grid container columnSpacing={2} rowSpacing={2}>
                          <Grid item xs={12} sm={3}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'fegliExtraAReduction',
                              label: 'Option A Reduction Option',
                              placeholder: 'Keep (or drop)',
                              inputType: 'select',
                              value: intakeState.fegliExtraAReduction,
                              onChange: updateIntakeStateAndSubmit(
                                (fegliExtraAReduction) =>
                                  updateIntakeStateAtKey({
                                    fegliExtraAReduction,
                                  })
                              ),
                              options: [
                                { value: 'keep', label: 'Keep' },
                                { value: 'drop', label: 'Drop' },
                              ],
                            })}
                          </Grid>
                          <Grid item xs={6} sm={3}></Grid>
                          <Grid item xs={6} sm={3} alignContent="end">
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'oac',
                              label: 'Option A Coverage After Reduction',
                              placeholder: 'Select Option A Reduction Option',
                              inputType: 'text',
                              value:
                                optionA &&
                                dollarFormat.format(
                                  optionA?.retirementCoverage
                                ),
                            })}
                          </Grid>
                          <Grid item xs={6} sm={3} alignContent="end">
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'oamc',
                              label: 'Option A Monthly Cost After Reduction',
                              placeholder: 'Select Option A Reduction Option',
                              inputType: 'text',
                              value:
                                optionA &&
                                dollarCentsFormat.format(
                                  optionA?.retirementPremium
                                ),
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {intakeState?.fegliOptionB && (
                    <>
                      <Grid
                        item
                        xs={12}
                        sm={2}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography variant="body2Medium">Option B</Typography>
                      </Grid>
                      <Grid item xs={12} sm={10}>
                        <Grid container columnSpacing={2} rowSpacing={2}>
                          <Grid item xs={12} sm={3}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'fegliExtraBKeep',
                              label: 'Option B How Many Will You Keep?',
                              placeholder: '(ranges from 0-5)',
                              inputType: 'select',
                              value: intakeState.fegliExtraBKeep,
                              onChange: updateIntakeStateAndSubmit(
                                (fegliExtraBKeep) =>
                                  updateIntakeStateAtKey({ fegliExtraBKeep })
                              ),
                              options: [
                                { value: 0, label: '0' },
                                { value: 1, label: '1' },
                                { value: 2, label: '2' },
                                { value: 3, label: '3' },
                                { value: 4, label: '4' },
                                { value: 5, label: '5' },
                              ],
                            })}
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'fegliExtraBReduction',
                              label: 'Option B Reduction',
                              placeholder:
                                'Select Option B Reduction (default none)',
                              inputType: 'select',
                              value: intakeState.fegliExtraBReduction,
                              onChange: updateIntakeStateAndSubmit(
                                (fegliExtraBReduction) =>
                                  updateIntakeStateAtKey({
                                    fegliExtraBReduction,
                                  })
                              ),
                              options: [
                                { value: 'none', label: 'No Reduction' },
                                { value: 'full', label: 'Full' },
                              ],
                            })}
                          </Grid>
                          <Grid item xs={6} sm={3} alignContent="end">
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'obc',
                              label: 'Option B Coverage',
                              placeholder: 'Select Option B Multiple',
                              inputType: 'text',
                              value:
                                optionB &&
                                dollarFormat.format(
                                  optionB?.retirementCoverage
                                ),
                            })}
                          </Grid>
                          <Grid item xs={6} sm={3} alignContent="end">
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'obmc',
                              label: 'Option B Cost',
                              placeholder: 'Select Option B Multiple',
                              inputType: 'text',
                              value:
                                optionB &&
                                dollarCentsFormat.format(
                                  optionB?.retirementPremium
                                ),
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {intakeState?.fegliOptionC && (
                    <>
                      <Grid
                        item
                        xs={12}
                        sm={2}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography variant="body2Medium">Option C</Typography>
                      </Grid>
                      <Grid item xs={12} sm={10}>
                        <Grid container columnSpacing={2} rowSpacing={2}>
                          <Grid item xs={12} sm={3}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'fegliExtraCKeep',
                              label: 'Option C How Many Will You Keep?',
                              placeholder: '(ranges from 0-5)',
                              inputType: 'select',
                              value: intakeState.fegliExtraCKeep,
                              onChange: updateIntakeStateAndSubmit(
                                (fegliExtraCKeep) =>
                                  updateIntakeStateAtKey({ fegliExtraCKeep })
                              ),
                              options: [
                                { value: 0, label: '0' },
                                { value: 1, label: '1' },
                                { value: 2, label: '2' },
                                { value: 3, label: '3' },
                                { value: 4, label: '4' },
                                { value: 5, label: '5' },
                              ],
                            })}
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            {renderMuiFormField({
                              autoFocus: false,
                              id: 'fegliExtraCReduction',
                              label: 'Option C Reduction',
                              placeholder:
                                'Select Option C Reduction (default none)',
                              inputType: 'select',
                              value: intakeState.fegliExtraCReduction,
                              onChange: updateIntakeStateAndSubmit(
                                (fegliExtraCReduction) =>
                                  updateIntakeStateAtKey({
                                    fegliExtraCReduction,
                                  })
                              ),
                              options: [
                                { value: 'none', label: 'No Reduction' },
                                { value: 'full', label: 'Full' },
                              ],
                            })}
                          </Grid>
                          <Grid item xs={6} sm={3} alignContent="end">
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'occ',
                              label: 'Option C Coverage',
                              placeholder: 'Select Option C Multiple',
                              inputType: 'text',
                              value:
                                optionC &&
                                dollarFormat.format(
                                  optionC?.retirementCoverage
                                ),
                            })}
                          </Grid>
                          <Grid item xs={6} sm={3} alignContent="end">
                            {renderMuiFormField({
                              autoFocus: false,
                              disabled: true,
                              id: 'ocmc',
                              label: 'Option C Cost',
                              placeholder: 'Select Option C Multiple',
                              inputType: 'text',
                              value:
                                optionC &&
                                dollarCentsFormat.format(
                                  optionC?.retirementPremium
                                ),
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <Typography variant="body2Medium">Monthly Total</Typography>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container columnSpacing={2} rowSpacing={2}>
                      <Grid item xs={12} sm={6}></Grid>
                      <Grid
                        item
                        xs={6}
                        sm={3}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography
                          variant="body2Medium"
                          sx={{
                            width: '100%',
                            borderTop: '2px solid #75787B',
                            paddingTop: 1,
                          }}
                        >
                          {dollarFormat.format(
                            fegliTotal?.retirementCoverage || 0
                          )}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sm={3}
                        display={'flex'}
                        alignItems={'center'}
                      >
                        <Typography
                          variant="body2Medium"
                          sx={{
                            width: '100%',
                            borderTop: '2px solid #75787B',
                            paddingTop: 1,
                          }}
                        >
                          {dollarCentsFormat.format(
                            fegliTotal?.retirementPremium || 0
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ background: '#ddd' }} />
                  </Grid>
                </>
              )}

              {intakeState?.dateOfBirth != null &&
                intakeState?.dateOfPlannedRetirement != null &&
                (intakeState?.privateLifeInsurance || intakeState?.fegli) && (
                  <Grid item xs={12}>
                    <LifeInsuranceSummary key="summary" adminView="true" />
                  </Grid>
                )}
              <Grid item xs={12}>
                <Button
                  variant="parcoRounded"
                  onClick={() =>
                    openModal('addLifeInsurance', {
                      intakeState: intakeState,
                      updateIntakeStateAndSubmit: updateIntakeStateAndSubmit,
                      updateIntakeStateAtKey: updateIntakeStateAtKey,
                    })
                  }
                >
                  Add More Life Insurance
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default Fegli
